import { APP_HUBS, Configuration, ENV_VARS, Environment } from '@app/models/config.model';
import { getEnvOrDefault } from '@app/v2/shared/utils';
import { ApplicationCard, ApplicationCardStaticConfiguration, PreviewAppId } from '@ra-state';
import { AppId } from '@rockwell-automation-inc/common-utils';
const DESIGN_HUB_LOGO = 'Design_Hub_logo.svg';
const OPERATIONS_HUB_LOGO = 'Operations_Hub_logo.svg';
const MAINTENANCE_HUB_LOGO = 'Maintenance_Hub_logo.svg';

const applications: ApplicationCardStaticConfiguration[] = [
  {
    appName: 'FactoryTalk Vault',
    appHub: APP_HUBS.DESIGN_HUB,
    appDescription:
      '<b>FactoryTalk® Vault</b> is a secure, centralized file storage solution for manufacturing design teams. With integrated, easy to use version and access controls, and the ability to upgrade, convert, and analyze automation project files in the cloud, your team can spend more time working on projects and less time searching for files or working from outdated versions.',
    appImage: 'Vault_2026_11_04.svg',
    hubLogo: DESIGN_HUB_LOGO,
    appId: AppId.Vault,
  },
  {
    appName: 'FactoryTalk Remote Access',
    appHub: APP_HUBS.OPERATIONS_HUB,
    appDescription:
      'FactoryTalk Remote Access is a web-based client that initiates a VPN connection and Remote Desktop to either the Stratix 4300 Router or Stratix RemoteAccess Runtime. FactoryTalk Remote Access provides centralized management of role-based users, groups, and devices. View audits and logs of domain activity and remote connections.',
    appImage: 'Remote_Access_2026_11_04.svg',
    hubLogo: OPERATIONS_HUB_LOGO,
    appId: AppId.SecureRemoteAccess,
  },
  {
    appName: 'Fiix',
    appHub: APP_HUBS.MAINTENANCE_HUB,
    appDescription:
      'Manage maintenance on 1000s of assets with a few clicks and fewer disruptions with Fiix by Rockwell Automation',
    appImage: 'Fiix_2026_11_04.svg',
    hubLogo: MAINTENANCE_HUB_LOGO,
    appId: PreviewAppId.Fiix,
  },
  {
    appName: 'FactoryTalk Design Studio',
    appHub: APP_HUBS.DESIGN_HUB,
    appDescription:
      'FactoryTalk® Design Studio™ is a new cloud-based software product built from the ground up to improve system design efficiency. Beginning with CompactLogix and ControlLogix design, FactoryTalk Design Studio reduces development time by leveraging modern software development practices, automated multi-user collaboration tools, and support for multiple controllers in a single project.',
    appImage: 'Design_Studio_2026_11_04.svg',
    hubLogo: DESIGN_HUB_LOGO,
    appId: AppId.IDE,
  },
  {
    appName: 'Foo (Integration Sample)',
    appHub: APP_HUBS.MAINTENANCE_HUB,
    appDescription: 'Sample Application to show CS integration',
    appImage: 'Foo_2025_04_01.svg',
    hubLogo: MAINTENANCE_HUB_LOGO,
    appId: AppId.FooService,
    supportsUniversalCredits: true,
  },
  {
    appName: 'Plex',
    appHub: APP_HUBS.OPERATIONS_HUB,
    appDescription:
      'The Plex Smart Manufacturing Platform is a digital system of record that connects people, systems, machines, and supply chains, automates business processes, tracks data from the shop floor to the top floor, and delivers analytics for unmatched visibility, quality, and control.',
    appImage: 'Plex_2026_11_04.svg',
    hubLogo: OPERATIONS_HUB_LOGO,
    appId: PreviewAppId.Plex,
  },
  {
    appName: 'FactoryTalk Optix',
    appHub: APP_HUBS.DESIGN_HUB,
    appDescription:
      'Welcome to a new open, scalable, modular visualization platform that provides options with modern technologies, extensibility and Industry 4.0 interoperability',
    appImage: 'Optix_2026_11_04.svg',
    hubLogo: DESIGN_HUB_LOGO,
    appId: AppId.FTOptix,
    isDefaultTrialEnabled: true,
  },
  {
    appName: 'FactoryTalk DataMosaix',
    appHub: APP_HUBS.OPERATIONS_HUB,
    appDescription:
      'FactoryTalk DataMosaix is an Industrial Data Platform that helps customers aggregate data from multiple sources and contextualize it so that it is more meaningful and provides deeper insights to their business.',
    appImage: 'Data_Mosaix_2026_11_04.svg',
    hubLogo: OPERATIONS_HUB_LOGO,
    appId: AppId.DataMosaix,
  },
  {
    appName: 'FactoryTalk Energy Manager',
    appHub: APP_HUBS.OPERATIONS_HUB,
    appDescription:
      'FactoryTalk Energy Manager is a web-based, multi-tenant application to monitor energy usage in the manufacturing Industry. It can monitor the usage of electricity, gas, water, and other energy source through an intuitive dashboard',
    appImage: 'Energy_Manager_2026_11_04.svg',
    hubLogo: OPERATIONS_HUB_LOGO,
    appId: AppId.EnergyManager,
  },
  {
    appName: 'FactoryTalk AssetIQ',
    appHub: APP_HUBS.MAINTENANCE_HUB,
    appDescription:
      'FactoryTalk AssetIQ centralizes OT device management across the enterprise by enabling automated device discovery, scalable firmware deployment, and lifecycle insights.',
    appImage: 'Asset_IQ_2026_11_04.svg',
    poweredBy: '',
    hubLogo: MAINTENANCE_HUB_LOGO,
    appId: AppId.AssetCenter,
    featureFlagName: 'tile_assetcenter',
  },
  {
    appName: 'PlantPAx Analytics',
    appHub: APP_HUBS.OPERATIONS_HUB,
    appDescription:
      "PlantPAx Analytics is a Rockwell Automation solution that delivers alarm & event KPI dashboards and reports in a single platform. Combine one or multiple site's data to unlock the full value of your process data.",
    appImage: 'PlantPAx_Analytics_2025_04_01.svg',
    hubLogo: OPERATIONS_HUB_LOGO,
    appId: AppId.PPaxAnalytics,
    featureFlagName: 'tile_ppax_analytics',
  },
  {
    appName: 'FactoryTalk Motion Analyzer',
    appHub: APP_HUBS.DESIGN_HUB,
    appDescription:
      'FactoryTalk® Motion Analyzer™ is a comprehensive motion-application design tool used for the analysis, optimization, selection, and validation of your motion control system. This software simplifies the machine design process, letting you quickly design and validate new machine concepts without purchasing or installing physical equipment. You can select from a wide range of Kinetix motors and drives, PowerFlex drives, and motors and gearboxes from our Technology Partners. The tool recommends solutions based on your application requirements and generates a bill of materials that can be exported into ProposalWorks for procurement.',
    appImage: 'Motion_Analyzer_2026_11_04.svg',
    hubLogo: DESIGN_HUB_LOGO,
    appId: PreviewAppId.FactoryTalkMotionAnalyzer,
    featureFlagName: 'tile_motion_analyzer',
  },
  {
    appName: 'FactoryTalk Analytics VisionAI',
    appHub: APP_HUBS.OPERATIONS_HUB,
    appDescription:
      'FactoryTalk Analytics VisionAI is an AI quality platform for manufacturing. It provides AI vision that identifies and classifies defects, delivering actionable insights used to improve quality and maximize yield',
    appImage: 'Analytics_Vision_AI_2026_11_04.svg',
    hubLogo: OPERATIONS_HUB_LOGO,
    appId: AppId.Elementary,
    featureFlagName: 'tile_elementary',
  },
  {
    appName: 'Advisor',
    appHub: APP_HUBS.DESIGN_HUB,
    appDescription: 'Product Selection & Configuration',
    appImage: 'Advisor_2026_11_04.svg',
    hubLogo: DESIGN_HUB_LOGO,
    appId: PreviewAppId.Advisor,
    featureFlagName: 'tile_advisor',
  },
  {
    appName: 'FactoryTalk Enterprise Performance Manager',
    appHub: APP_HUBS.OPERATIONS_HUB,
    appDescription: 'FactoryTalk Metrics Enterprise Performance Manager',
    appImage: 'Enterprise_Performance_Manager_2025_04_01.svg',
    hubLogo: OPERATIONS_HUB_LOGO,
    appId: AppId.MEPM,
    featureFlagName: 'tile_mepm',
  },
];
const applicationMap: Record<string, ApplicationCardStaticConfiguration> = Object.fromEntries(
  applications.map((item) => [item.appId.toString(), item]),
);

export function createEnvConfig(config: Partial<Configuration>, enabledApps: Partial<ApplicationCard>[]): Environment {
  const applications = enabledApps.map((app) => {
    if (app.appId === undefined) {
      throw new Error('Enabled application must define an appId');
    }
    const appId = app.appId.toString();
    if (!Object.hasOwn(applicationMap, appId)) {
      throw new Error(`Enabled application appId is not configured: ${appId}`);
    }
    const staticConfiguration = applicationMap[appId];
    return { ...staticConfiguration, ...app };
  }) as ApplicationCard[];
  const names: Record<string, ApplicationCard> = applications.reduce(
    (map, app) => {
      map[app.appId] = app;
      return map;
    },
    {} as Record<string, ApplicationCard>,
  );
  // env overrides from index-html-transform.local.ts
  config.notificationsBaseUrl = getEnvOrDefault(ENV_VARS.CS_NOTIFICATION_API_BASE_URL, config.notificationsBaseUrl);
  config.csApiBaseUrl = getEnvOrDefault(ENV_VARS.CS_API_BASE_URL, config.csApiBaseUrl);
  config.csPortalUrl = getEnvOrDefault(ENV_VARS.CS_PORTAL_URL, config.csPortalUrl);

  if (config.authConfig && !config.authConfig.redirectUri) {
    config.authConfig.redirectUri = `${config.csPortalUrl}/callback`;
  }
  const appConfig = {
    ...config,
    get applications(): ApplicationCard[] {
      return applications;
    },
    appName(id: string): string {
      return names[id]?.appName || id;
    },

    isApiCall(url: string): boolean {
      try {
        const parsedUrl = new URL(url);
        return parsedUrl.href.startsWith(`${config.csApiBaseUrl}/api/`);
        // eslint-disable-next-line sonarjs/no-ignored-exceptions
      } catch (_ex) {
        return url.startsWith('/api/');
      }
    },
  } as Configuration;

  return {
    production: appConfig.production || false,
    appConfiguration: appConfig,
  };
}
